import { useQuery, useInfiniteQuery, queryOptions } from "@tanstack/react-query";
import { getSupabase } from "@/integrations/supabase/lazy";
import { expandRvTypeFamily } from "@/data/masterRvTypes";
import { inventoryPageRanges } from "@/lib/inventoryOrdering";
import {
  PAYMENT_FACTOR,
  PAYMENT_BUCKETS,
  WEIGHT_BUCKETS,
  LENGTH_BUCKETS,
  SLEEPS_BUCKETS,
  PRICE_BUCKETS,
  bucketsToRanges,
} from "@/lib/inventoryFilterDefs";

/**
 * Slim column set for card/list views. Excludes heavy text columns
 * (description, features[], floorplan_features[], specs, etc.) that
 * the homepage / search grid never read. Detail page still uses select("*").
 */
const INVENTORY_CARD_COLUMNS =
  "id,stock_number,title,slug,condition,status,rv_type,year,make,model," +
  "manufacturer,length_ft,weight_lbs,sleeps,price,sale_price,images," +
  "floorplan_image,is_featured,is_special,special_label,certified,show_best_price_button,created_at";

export interface RVUnit {
  id: string;
  stock_number: string;
  title: string;
  slug: string;
  condition: string;
  status: string;
  rv_type: string;
  year: number | null;
  make: string | null;
  model: string | null;
  trim: string | null;
  manufacturer: string | null;
  length_ft: number | null;
  weight_lbs: number | null;
  sleeps: number | null;
  slides: number | null;
  price: number | null;
  sale_price: number | null;
  monthly_payment: number | null;
  mileage: number | null;
  exterior_color: string | null;
  interior_color: string | null;
  vin: string | null;
  description: string | null;
  features: string[];
  floorplan_features: string[];
  images: string[];
  floorplan_image: string | null;
  video_url: string | null;
  walkthrough_url: string | null;
  fuel_type: string | null;
  engine: string | null;
  chassis: string | null;
  hitch_weight_lbs: number | null;
  cargo_weight_lbs: number | null;
  fresh_water_gal: number | null;
  grey_water_gal: number | null;
  black_water_gal: number | null;
  awning_length_ft: number | null;
  ac_btu: number | null;
  furnace_btu: number | null;
  water_heater: string | null;
  refrigerator: string | null;
  axle_count: number | null;
  tire_size: string | null;
  is_featured: boolean;
  is_special: boolean;
  is_deal_of_week: boolean;
  special_label: string | null;
  certified: boolean | null;
  unit_library_id: string | null;
  location: string | null;
  window_sticker_url: string | null;
  show_best_price_button?: boolean | null;
}

export interface UnitLibrary {
  id: string;
  year: number;
  make: string;
  model: string;
  trim: string | null;
  full_description: string | null;
  feature_exterior: string[];
  feature_interior: string[];
  feature_kitchen: string[];
  feature_bedroom: string[];
  feature_bathroom: string[];
  feature_entertainment: string[];
  feature_construction: string[];
  feature_safety: string[];
  feature_climate: string[];
  feature_electrical: string[];
  feature_solar: string[];
  specs: Record<string, any>;
  highlights: string[];
  seo_description: string | null;
  brochure_url: string | null;
  hero_image_url: string | null;
  packages: any[];
}

/** Fetch sorted images from unit_images and merge into units */
async function mergeSortedImages(
  units: RVUnit[],
  opts?: { skip?: boolean }
): Promise<RVUnit[]> {
  if (opts?.skip || units.length === 0) return units;
  const unitIds = units.map((u) => u.id);
  const sb = await getSupabase();

  // Supabase .in() has a practical limit; chunk if needed
  const chunkSize = 200;
  const chunks: string[][] = [];
  for (let i = 0; i < unitIds.length; i += chunkSize) {
    chunks.push(unitIds.slice(i, i + chunkSize));
  }

  const results = await Promise.all(
    chunks.map((chunk) =>
      sb
        .from("unit_images")
        .select("unit_id, local_path_full, local_path_thumb")
        .in("unit_id", chunk)
        .order("sort_order", { ascending: true })
    )
  );

  const allImages: { unit_id: string; local_path_full: string | null; local_path_thumb: string | null }[] = [];
  for (const { data } of results) {
    if (data) allImages.push(...(data as typeof allImages));
  }

  // Build map: unit_id → sorted image URLs (prefer full, fall back to thumb)
  const imageMap = new Map<string, string[]>();
  for (const img of allImages) {
    const url = img.local_path_full || img.local_path_thumb;
    if (!url) continue;
    const list = imageMap.get(img.unit_id) ?? [];
    list.push(url);
    imageMap.set(img.unit_id, list);
  }

  return units.map((unit) => {
    const sorted = imageMap.get(unit.id);
    const origLen = unit.images?.length ?? 0;
    return sorted?.length && sorted.length >= origLen
      ? { ...unit, images: sorted }
      : unit;
  });
}

/* ------------------------------------------------------------------ */
/*  Server-side paginated & filtered inventory hook                    */
/* ------------------------------------------------------------------ */

export interface InventoryFilters {
  condition?: "all" | "new" | "used";
  rvType?: string;        // slug form e.g. "travel-trailer"
  feature?: string;       // slug form e.g. "king-bed"
  featureLabel?: string | undefined;  // display form e.g. "King Bed"
  rvTypeLabel?: string | undefined;   // display form e.g. "Travel Trailer"
  searchTerm?: string | undefined;
  sort?: string;
  // multi-select arrays (preferred over single-value above)
  types?: string[];          // display labels e.g. ["Travel Trailer","Fifth Wheel"]
  features?: string[];       // display labels (combined with bedTypes via overlaps)
  bedTypes?: string[];       // display labels stored in floorplan_features
  manufacturers?: string[];
  makes?: string[];
  years?: number[];
  // bucket id arrays
  paymentBuckets?: string[];
  weightBuckets?: string[];
  lengthBuckets?: string[];
  sleepsBuckets?: string[];
  priceBuckets?: string[];
  // boolean flags
  featured?: boolean;
  special?: boolean;
  dealOfWeek?: boolean;
  managerSpecials?: boolean;
  // ranges
  weightMin?: number | undefined;
  weightMax?: number | undefined;
  sleepsMin?: number | undefined;
  sleepsMax?: number | undefined;
  paymentMin?: number | undefined;
  paymentMax?: number | undefined;
  priceMin?: number | undefined;
  priceMax?: number | undefined;
}

const PAGE_SIZE = 24;
const FIRST_PAGE_SIZE = 8;
/** Cap for the single fetch used in keyword-search mode. */
const SEARCH_FETCH_LIMIT = 500;
/** Extra columns only needed to rank keyword searches client-side. */
const SEARCH_EXTRA_COLUMNS =
  ",trim,vin,description,features,floorplan_features,exterior_color,interior_color";

export function usePagedInventory(filters: InventoryFilters) {
  return useInfiniteQuery({
    queryKey: ["inventory-paged", "incoming-last", filters],
    queryFn: async ({ pageParam }) => {
      // Only request an exact COUNT(*) on the first page — it's expensive
      // and the total doesn't change while the user scrolls. Subsequent
      // pages reuse the count cached on page 0.
      const isFirstPage = pageParam.page === 0;
      // Keyword-search mode: fetch the full (filtered) set in one bounded
      // query and let the client rank it with rankSearch(). Keyword search
      // covers fields a PostgREST ILIKE can't reach well (rv_type, features,
      // description, synonyms like "motorhome"), and the active inventory is
      // small enough that one fetch beats paginating partial results.
      const searching = !!filters.searchTerm?.trim();
      const sb = await getSupabase();
      const createQuery = (statuses: string[], countRows = false, head = false) => {
      let query = sb
        .from("rv_inventory")
        .select(
          INVENTORY_CARD_COLUMNS + (searching ? SEARCH_EXTRA_COLUMNS : ""),
          countRows ? { count: "exact", head } : {}
        )
        .in("status", statuses);

      // --- server-side filters ---
      if (filters.condition === "new") query = query.eq("condition", "new");
      if (filters.condition === "used") query = query.eq("condition", "used");

      // Multi-select Type takes precedence over single rvTypeLabel.
      // (Type/feature filters stay active while searching.)
      if (filters.types && filters.types.length > 0) {
        const expanded = Array.from(
          new Set(filters.types.flatMap((t) => expandRvTypeFamily(t))),
        );
        query = query.in("rv_type", expanded);
      } else if (filters.rvTypeLabel && filters.rvTypeLabel !== "All Types") {
        // A base motorized class must include its fuel variants, e.g.
        // "Motor Home Class A" also returns "Motor Home Class A - Diesel".
        const family = expandRvTypeFamily(filters.rvTypeLabel);
        query = family.length > 1
          ? query.in("rv_type", family)
          : query.eq("rv_type", filters.rvTypeLabel);
      }

      // Combine multi-select features + bed types into a single overlaps() call
      const featLabels = [
        ...(filters.features ?? []),
        ...(filters.bedTypes ?? []),
      ];
      if (featLabels.length > 0) {
        query = query.overlaps("floorplan_features", featLabels);
      } else if (filters.featureLabel && filters.featureLabel !== "All Features") {
        query = query.contains("floorplan_features", [filters.featureLabel]);
      }

      // Case-insensitive match so "EAST TO WEST" and "East To West" both hit.
      const escapeIlike = (s: string) =>
        s.replace(/([\\,()])/g, "\\$1");
      if (filters.manufacturers && filters.manufacturers.length > 0) {
        const or = filters.manufacturers
          .map((m) => `manufacturer.ilike.${escapeIlike(m)}`)
          .join(",");
        query = query.or(or);
      }
      if (filters.makes && filters.makes.length > 0) {
        const or = filters.makes
          .map((m) => `make.ilike.${escapeIlike(m)}`)
          .join(",");
        query = query.or(or);
      }
      if (filters.years && filters.years.length > 0) {
        query = query.in("year", filters.years);
      }

      // Bucket-based OR ranges. Each .or() call AND's with the rest.
      const applyBucketRanges = (
        ids: string[] | undefined,
        defs: typeof PAYMENT_BUCKETS,
        column: string,
        convert: (n: number) => number = (n) => n,
      ) => {
        if (!ids || ids.length === 0) return;
        const ranges = bucketsToRanges(ids, defs);
        if (ranges.length === 0) return;
        const parts = ranges.map(({ min, max }) => {
          const conds: string[] = [];
          if (min !== undefined) conds.push(`${column}.gte.${convert(min)}`);
          if (max !== undefined) conds.push(`${column}.lte.${convert(max)}`);
          return conds.length > 1 ? `and(${conds.join(",")})` : conds[0];
        });
        query = query.or(parts.join(","));
      };

      applyBucketRanges(filters.weightBuckets, WEIGHT_BUCKETS, "weight_lbs");
      applyBucketRanges(filters.lengthBuckets, LENGTH_BUCKETS, "length_ft");
      applyBucketRanges(filters.sleepsBuckets, SLEEPS_BUCKETS, "sleeps");
      applyBucketRanges(filters.priceBuckets, PRICE_BUCKETS, "sale_price");
      // Payment buckets → translate $/mo → sale_price equivalent
      applyBucketRanges(
        filters.paymentBuckets,
        PAYMENT_BUCKETS,
        "sale_price",
        (n) => Math.round(n / PAYMENT_FACTOR),
      );

      if (filters.featured) query = query.eq("is_featured", true);
      if (filters.special) query = query.eq("is_special", true);
      if (filters.dealOfWeek) query = query.eq("is_deal_of_week", true);
      if (filters.managerSpecials) {
        query = query.or("is_special.eq.true,is_featured.eq.true,is_deal_of_week.eq.true");
      }

      if (filters.weightMin) query = query.gte("weight_lbs", filters.weightMin);
      if (filters.weightMax) query = query.lte("weight_lbs", filters.weightMax);
      if (filters.sleepsMin) query = query.gte("sleeps", filters.sleepsMin);
      if (filters.sleepsMax) query = query.lte("sleeps", filters.sleepsMax);
      // Payment filter: monthly_payment is not populated in inventory data,
      // so translate the requested payment range into an equivalent sale_price
      // range using the same defaults as the Payment Calculator
      // (120 months @ 8% APR → factor ≈ 0.012133).
      if (filters.paymentMin) {
        query = query.gte("sale_price", Math.round(filters.paymentMin / PAYMENT_FACTOR));
      }
      if (filters.paymentMax) {
        query = query.lte("sale_price", Math.round(filters.paymentMax / PAYMENT_FACTOR));
      }
      if (filters.priceMin) query = query.gte("sale_price", filters.priceMin);

      if (filters.priceMax) query = query.lte("sale_price", filters.priceMax);

      // --- sort ---
      switch (filters.sort) {
        case "price-low":
          query = query.order("sale_price", { ascending: true, nullsFirst: false });
          break;
        case "price-high":
          query = query.order("sale_price", { ascending: false, nullsFirst: false });
          break;
        case "year-new":
          query = query.order("year", { ascending: false, nullsFirst: false });
          break;
        case "length":
          query = query.order("length_ft", { ascending: false, nullsFirst: false });
          break;
        case "weight":
          query = query.order("weight_lbs", { ascending: true, nullsFirst: false });
          break;
        default: // "featured"
          query = query
            .order("is_featured", { ascending: false })
            .order("created_at", { ascending: false });
      }

      // Stable ties prevent duplicate/skipped rows between pages.
      return query.order("id", { ascending: true });
      };

      // Keyword relevance is partitioned after ranking in the page component.
      if (searching) {
        const { data, error, count } = await createQuery(["available", "pending", "coming_soon"], true).limit(SEARCH_FETCH_LIMIT);
        if (error) throw error;
        return { units: (data ?? []) as unknown as RVUnit[], totalCount: count ?? 0, inStockCount: 0, page: pageParam.page };
      }

      const offset = isFirstPage ? 0 : FIRST_PAGE_SIZE + (pageParam.page - 1) * PAGE_SIZE;
      const size = isFirstPage ? FIRST_PAGE_SIZE : PAGE_SIZE;
      let inStockCount = pageParam.inStockCount;
      let totalCount = pageParam.totalCount;
      let units: RVUnit[] = [];
      if (isFirstPage) {
        const [stock, incoming] = await Promise.all([
          createQuery(["available", "pending"], true).range(0, size - 1),
          createQuery(["coming_soon"], true, true),
        ]);
        if (stock.error) throw stock.error;
        if (incoming.error) throw incoming.error;
        inStockCount = stock.count ?? 0;
        totalCount = inStockCount + (incoming.count ?? 0);
        units = (stock.data ?? []) as unknown as RVUnit[];
      }
      const ranges = inventoryPageRanges(offset, size, inStockCount);
      const rows = await Promise.all([
        !isFirstPage && ranges.stock
          ? createQuery(["available", "pending"]).range(ranges.stock.from, ranges.stock.to)
          : Promise.resolve({ data: [], error: null }),
        ranges.incoming && offset + units.length < totalCount
          ? createQuery(["coming_soon"]).range(ranges.incoming.from, ranges.incoming.to)
          : Promise.resolve({ data: [], error: null }),
      ]);
      for (const result of rows) {
        if (result.error) throw result.error;
        units.push(...((result.data ?? []) as unknown as RVUnit[]));
      }
      return {
        units,
        totalCount,
        inStockCount,
        page: pageParam.page,
      };
    },
    initialPageParam: { page: 0, inStockCount: 0, totalCount: 0 },
    getNextPageParam: (lastPage, allPages) => {
      // Search mode returns everything in a single page.
      if (filters.searchTerm?.trim()) return undefined;
      const nextPage = lastPage.page + 1;
      const expected = lastPage.page === 0 ? FIRST_PAGE_SIZE : PAGE_SIZE;
      if (lastPage.units.length < expected) return undefined;
      return { page: nextPage, inStockCount: lastPage.inStockCount, totalCount: lastPage.totalCount };
    },
  });
}

/* ------------------------------------------------------------------ */
/*  Legacy hooks (kept for other pages like Index, InventoryDetail)    */
/* ------------------------------------------------------------------ */

export function useInventoryList(enabled = true) {
  return useQuery({
    queryKey: ["inventory"],
    enabled,
    queryFn: async () => {
      const sb = await getSupabase();
      const { data, error } = await sb
        .from("rv_inventory")
        .select(INVENTORY_CARD_COLUMNS)
        .order("is_featured", { ascending: false })
        .order("created_at", { ascending: false });
      if (error) throw error;
      const units = (data ?? []) as unknown as RVUnit[];
      return mergeSortedImages(units, { skip: true });
    },
  });
}

/**
 * Lightweight homepage bundle: the 12 "ready today" cards plus tiny
 * status/type/price rows for the tile & budget counts. Replaces the old
 * full-table useInventoryList fetch on the CarMax home — the counts only
 * need three narrow columns, never images or card data.
 */
export function useInventoryHome(enabled = true) {
  return useQuery({
    queryKey: ["inventory-home"],
    enabled,
    queryFn: async () => {
      const sb = await getSupabase();
      const [cards, counts] = await Promise.all([
        sb
          .from("rv_inventory")
          .select(INVENTORY_CARD_COLUMNS)
          .eq("status", "available")
          .order("is_featured", { ascending: false })
          .order("created_at", { ascending: false })
          .limit(12),
        sb
          .from("rv_inventory")
          .select("status,rv_type,price,sale_price"),
      ]);
      if (cards.error) throw cards.error;
      if (counts.error) throw counts.error;
      return {
        available: (cards.data ?? []) as unknown as RVUnit[],
        counts: (counts.data ?? []) as { status: string; rv_type: string | null; price: number | null; sale_price: number | null }[],
      };
    },
  });
}

export function useInventoryFeatured() {
  return useQuery({
    queryKey: ["inventory-featured"],
    queryFn: async () => {
      const sb = await getSupabase();
      const { data, error } = await sb
        .from("rv_inventory")
        .select(INVENTORY_CARD_COLUMNS)
        .eq("is_featured", true)
        .order("created_at", { ascending: false })
        .limit(10);
      if (error) throw error;
      const units = (data ?? []) as unknown as RVUnit[];
      // Home page only renders images[0] — skip the extra unit_images round-trip.
      return mergeSortedImages(units, { skip: true });
    },
  });
}

/** Shared options so route loaders can prime the same cache entry the
 *  component subscribes to (SSR paints the unit + hero from HTML). */
export function inventoryUnitQueryOptions(slug: string) {
  return queryOptions({
    queryKey: ["inventory", slug],
    // Reuse SSR data during hydration; explicit admin invalidation still refetches.
    staleTime: 60_000,
    queryFn: async () => {
      const sb = await getSupabase();
      const { data, error } = await sb
        .from("rv_inventory")
        .select("*")
        .eq("slug", slug)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      // Merge in the authoritative ordered image list from `unit_images` so
      // any drift / stale entries in `rv_inventory.images` (e.g. URLs with a
      // bad `backups/` prefix) don't break the gallery & lightbox.
      const merged = await mergeSortedImages([data as unknown as RVUnit]);
      return (merged[0] ?? (data as unknown as RVUnit)) as RVUnit;
    },
    enabled: !!slug,
  });
}

export function useInventoryUnit(slug: string) {
  return useQuery(inventoryUnitQueryOptions(slug));
}

export function useUnitLibrary(libraryId: string | null | undefined) {
  return useQuery({
    queryKey: ["unit-library", libraryId],
    queryFn: async () => {
      const sb = await getSupabase();
      const { data, error } = await sb
        .from("unit_library")
        .select("*")
        .eq("id", libraryId!)
        .maybeSingle();
      if (error) throw error;
      return data as unknown as UnitLibrary | null;
    },
    enabled: !!libraryId,
  });
}
